import React, { useState } from "react";

import uploadIcon from "../../assets/images/uploadIcon.png";
import axios, { AxiosError } from "axios";
import { getAccessToken } from "../../auth/getToken";
import { msalInstance } from "../../auth/msalInstance";
import {
  bulkCreateItems,
  createSpList,
  createTextColumnWithRetry,
  deleteAllItems,
  findListIdByName,
  getAllListItems,
  getExistingColumns,
} from "../../services/sharepointService";
import { getConfig } from "../../services/configService";

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";
const scopes = ["Sites.ReadWrite.All", "Sites.Manage.All"];

/* ------------------------ helpers: retry / throttle ------------------------ */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const isThrottle = (err: any) => {
  const code =
    err?.response?.status || err?.status || err?.code || err?.response?.data?.error?.code;
  const message =
    err?.response?.data?.error?.message || err?.message || "";

  // HTTP 429/503 or Graph "throttledRequest" / "activityLimitReached"
  return (
    Number(code) === 429 ||
    Number(code) === 503 ||
    /throttledRequest|activityLimitReached|quota/i.test(String(code)) ||
    /throttled/i.test(message)
  );
};

async function withRetry<T>(
  fn: () => Promise<T>,
  onLog: (m: string) => void,
  label: string,
  { retries = 6, base = 600 }: { retries?: number; base?: number } = {}
): Promise<T> {
  let attempt = 0;
  while (true) {
    try {
      return await fn();
    } catch (err: any) {
      if (!isThrottle(err) || attempt >= retries) throw err;
      const wait = Math.ceil(base * Math.pow(2, attempt) + Math.random() * 250);
      onLog(
        `⏳ Throttled during ${label}. Backing off ${wait}ms (attempt ${attempt + 1}/${retries + 1})`
      );
      await sleep(wait);
      attempt++;
    }
  }
}

/* ------------------------------ props / comp ------------------------------- */
interface Props {
  data: any[];
  phase: string;
  projectName: string;
  siteId: string | null;
  isPersonal: boolean;
  siteUrl: string;
  onLog: (msg: string) => void;
  onUploadComplete: () => void;
}

const SharePointUploader: React.FC<Props> = ({
  data,
  phase,
  projectName,
  siteId,
  onLog,
  onUploadComplete,
}) => {
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState("");

  const purgeListCompletely = async (
    sId: string,
    lId: string,
    token: string,
    label: string
  ) => {
    const MAX_ROUNDS = 6;
    for (let round = 1; round <= MAX_ROUNDS; round++) {
      const before = await withRetry(
        () => getAllListItems(sId, lId, token),
        onLog,
        `${label}: getAllListItems`
      );
      if (!before.length) {
        onLog(`🧼 ${label}: empty after purge`);
        return;
      }
      onLog(`🧹 ${label}: deleting ${before.length} items (round ${round}/${MAX_ROUNDS})`);
      await withRetry(
        () => deleteAllItems(sId, lId, token),
        onLog,
        `${label}: deleteAllItems`
      );
      // brief pause to let SP settle
      await sleep(400);
    }
    onLog(`⚠️ ${label}: purge rounds exhausted; continuing`);
  };

  const handleUpload = async () => {
    if (!data.length || !projectName || !siteId) {
      setStatus("Missing data, project name, or site ID.");
      return;
    }
    setUploading(true);

    try {
      // 1) Auth
      const token = await getAccessToken(msalInstance, scopes);
      if (!token) throw new Error("Token acquisition failed.");

      // 2) Prepare “changes” list
      const changesName = `changes_${projectName.trim()}_${phase.trim()}`;
      onLog(`🔧 Preparing list: ${changesName}`);

      let changesId = await withRetry(
        () => findListIdByName(siteId, changesName, token),
        onLog,
        "find changes list"
      );
      if (!changesId) {
        onLog("➕ Creating changes list…");
        changesId = await withRetry(
          () => createSpList(siteId, changesName, token),
          onLog,
          "create changes list"
        );
      }
      if (!changesId) throw new Error(`Cannot find/create '${changesName}'`);

      await purgeListCompletely(siteId, changesId, token, "Changes list");

      // Ensure columns exist
      const existingRawCols = await withRetry(
        () => getExistingColumns(siteId, changesId!, token),
        onLog,
        "getExistingColumns(changes)"
      );

      const displayCols = Object.keys(data[0] || {});
      onLog(`📑 Data columns: [${displayCols.join(", ")}]`);
      for (const col of displayCols) {
        if (!existingRawCols[col]) {
          await withRetry(
            () => createTextColumnWithRetry(siteId, changesId!, col, token),
            onLog,
            `createTextColumn '${col}'`
          );
        }
      }

      // 3) Bulk insert raw rows (reduced pressure: concurrency=1)
      onLog(`📤 Bulk-inserting ${data.length} raw change rows…`);
      const rawItems = data.map((row, i) => {
        const fields: Record<string, string> = { Title: `Row_${i + 1}` };
        for (const col of displayCols) {
          fields[col] = row[col] === "---" ? "" : String(row[col] ?? "");
        }
        return { fields };
      });
      await withRetry(
        () => bulkCreateItems(siteId, changesId!, token, rawItems, 1),
        onLog,
        "bulkCreateItems(changes)"
      );
      onLog(`✅ Raw upload complete: ${data.length}/${data.length}`);

      // 4) Load QuestionTemplates (case-tolerant & with backoff)
      const cfg = getConfig();
      const qListId = cfg.questionsListId;
      if (!qListId) {
        onLog("⚠️ No QuestionTemplates configured—skipping CQS");
        onUploadComplete();
        return;
      }

      onLog("🔍 Fetching QuestionTemplates…");
      const tmplResp = await withRetry(
        () =>
          axios.get(
            `${GRAPH_BASE}/sites/${siteId}/lists/${qListId}/items?$top=2000&$select=id&$expand=fields`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        onLog,
        "GET QuestionTemplates"
      );

      const rawTemplates: any[] = (tmplResp.data?.value || []).map(
        (it: any) => it.fields || {}
      );

      // Normalize template fields (support QuestionId and Questionid, etc.)
      const normalized = rawTemplates
        .map((t) => {
          const qid = (t.Questionid ?? t.QuestionId ?? t.questionid ?? t.questionId);
          return {
            Questionid: qid != null ? String(qid).trim() : "",
            Question: t.Question ?? t.question ?? "",
            TriggerOn: t.TriggerOn ?? t.triggeron ?? "",
            ResponsableEmail: t.ResponsableEmail ?? t.responsableemail ?? "",
            SendIntervalValue: t.SendIntervalValue ?? t.sendintervalvalue ?? "",
            SendIntervalUnit: t.SendIntervalUnit ?? t.sendintervalunit ?? "",
            Action: t.Action ?? t.action ?? "",
            Responsiblerole: t.Responsiblerole ?? t.responsiblerole ?? "",
            emailbody: t.emailbody ?? t.EmailBody ?? "",
            emailsubject: t.emailsubject ?? t.EmailSubject ?? "",
          };
        })
        .filter((t) => t.Questionid);

      // Deduplicate by Questionid
      const dedupTemplates = Array.from(
        new Map(normalized.map((t) => [t.Questionid, t])).values()
      );
      onLog(`✅ Valid templates: ${dedupTemplates.length} (deduped from ${rawTemplates.length})`);

      if (!dedupTemplates.length) {
        onLog("⚠️ No valid templates—skipping CQS");
        onUploadComplete();
        return;
      }

      // 5) Build unique (change, area) pairs
      const changeAreaPairs = Array.from(
        new Map(
          data
            .map((r) => ({
              change: String(r.Processnumber ?? "").trim(),
              area: String(r.SheetName ?? "").trim(),
            }))
            .filter((x) => x.change && x.area)
            .map((x) => [`${x.change}||${x.area}`, x])
        ).values()
      );

      // 6) Prepare & clear CQS list
      const cqsName = `ChangeQuestionStatus_${projectName.trim()}`;
      onLog(`🔧 Preparing CQS list: ${cqsName}`);

      let cqsId = await withRetry(
        () => findListIdByName(siteId, cqsName, token),
        onLog,
        "find CQS list"
      );
      if (!cqsId) {
        onLog("➕ Creating CQS list…");
        cqsId = await withRetry(
          () => createSpList(siteId, cqsName, token),
          onLog,
          "create CQS list"
        );
      }
      if (!cqsId) throw new Error(`Cannot find/create '${cqsName}'`);

      // ensure required columns exist
      const neededCols = [
        "ChangeNumber",
        "QuestionId",
        "Area",
        "responseReceived",
        "lastSent",
        "lastChecked",
        "conversationId",
        "internetMessageId",
        "Question",
        "TriggerOn",
        "ResponsableEmail",
        "SendIntervalValue",
        "SendIntervalUnit",
        "Action",
        "Responsiblerole",
        "emailbody",
        "emailsubject",
        "replySubject",
        "replyBody",
        "replyReceivedDate",
      ];
      const existingCqsCols = await withRetry(
        () => getExistingColumns(siteId, cqsId!, token),
        onLog,
        "getExistingColumns(CQS)"
      );
      for (const col of neededCols) {
        if (!existingCqsCols[col]) {
          await withRetry(
            () => createTextColumnWithRetry(siteId, cqsId!, col, token),
            onLog,
            `createTextColumn(CQS '${col}')`
          );
          onLog(`➕ Created CQS column '${col}'`);
        }
      }
      const cqsColMap = await withRetry(
        () => getExistingColumns(siteId, cqsId!, token),
        onLog,
        "refresh getExistingColumns(CQS)"
      );

      // hard purge CQS to avoid dupes
      await purgeListCompletely(siteId, cqsId!, token, "CQS list");

      // 7) Bulk insert CQS rows (idempotent titles, reduced pressure)
      const totalCqs = changeAreaPairs.length * dedupTemplates.length;
      onLog(`📤 Bulk-inserting ${totalCqs} CQS rows…`);

      const cqsItems: Array<{ fields: Record<string, string> }> = [];
      for (const { change, area } of changeAreaPairs) {
        for (const t of dedupTemplates) {
          const title = `${change}-${t.Questionid}`; // idempotent
          const fields: Record<string, string> = {
            Title: title,
            [cqsColMap["ChangeNumber"] || "ChangeNumber"]: change,
            [cqsColMap["Area"] || "Area"]: area,
            [cqsColMap["QuestionId"] || "QuestionId"]: t.Questionid,
            [cqsColMap["responseReceived"] || "responseReceived"]: "false",
            [cqsColMap["lastSent"] || "lastSent"]: "",
            [cqsColMap["lastChecked"] || "lastChecked"]: "",
            [cqsColMap["conversationId"] || "conversationId"]: "",
            [cqsColMap["internetMessageId"] || "internetMessageId"]: "",
            [cqsColMap["Question"] || "Question"]: t.Question,
            [cqsColMap["TriggerOn"] || "TriggerOn"]: t.TriggerOn,
            [cqsColMap["ResponsableEmail"] || "ResponsableEmail"]: t.ResponsableEmail,
            [cqsColMap["SendIntervalValue"] || "SendIntervalValue"]: String(
              t.SendIntervalValue ?? ""
            ),
            [cqsColMap["SendIntervalUnit"] || "SendIntervalUnit"]: t.SendIntervalUnit || "",
            [cqsColMap["Action"] || "Action"]: t.Action || "",
            [cqsColMap["Responsiblerole"] || "Responsiblerole"]: t.Responsiblerole || "",
            [cqsColMap["emailbody"] || "emailbody"]: t.emailbody || "",
            [cqsColMap["emailsubject"] || "emailsubject"]: t.emailsubject || "",
          };
          cqsItems.push({ fields });
        }
      }

      await withRetry(
        () => bulkCreateItems(siteId, cqsId!, token, cqsItems, 1),
        onLog,
        "bulkCreateItems(CQS)"
      );
      onLog(`✅ Completed CQS upload: ${cqsItems.length}/${cqsItems.length}`);

      onUploadComplete();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message ||
        (err as AxiosError)?.message ||
        String(err);
      setStatus(`❌ Upload failed: ${msg}`);
      onLog(`❌ Upload failed: ${msg}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4 flex flex-col items-center">
      <button
        onClick={handleUpload}
        disabled={uploading}
        className="w-72 h-72 bg-white/20 backdrop-blur-md rounded-2xl shadow-lg hover:bg-white/30 hover:scale-105 transition-transform duration-300 ease-in-out flex flex-col items-center justify-center text-white"
      >
        <img src={uploadIcon} alt="Upload" className="h-48 w-38 mb-6 object-contain" />
        <span className="text-xl font-semibold">
          {uploading ? "Uploading..." : "Upload to SharePoint"}
        </span>
      </button>

      {status && (
        <p
          className={`text-sm ${
            status.startsWith("✅")
              ? "text-green-400"
              : status.startsWith("❌")
              ? "text-red-400"
              : "text-yellow-200"
          }`}
        >
          {status}
        </p>
      )}
    </div>
  );
};

export default SharePointUploader;
